import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FormField, GhostButton, Select } from "../../../design-system";
import { borders, surfaces, textColors, typography } from "../../../design-system/tokens";
import {
  ALL_DAYS,
  HOUR_OPTIONS,
  WEEKDAYS,
  WEEKDAY_DAYS,
  WEEKEND_DAYS,
  computeNextRun,
  formatRelativeTime,
  formatRunDays,
  formatUtcDateTime,
} from "./automationUtils";

const sameDays = (a, b) => {
  const x = [...a].sort().join(",");
  const y = [...b].sort().join(",");
  return x === y;
};

/**
 * Routine trigger: which weekdays (Mon=0 … Sun=6) and which hour (UTC) the
 * scheduler evaluates the situation. Shows the next run computed client-side.
 */
const SchedulePicker = ({ days = [], hour = "9", onChange, disabled = false, errors = {} }) => {
  const [now, setNow] = useState(() => new Date());

  // Keep the "next run" hint honest while the editor stays open.
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const nextRun = useMemo(() => computeNextRun(days, hour, now), [days, hour, now]);

  const toggleDay = (value) => {
    const set = new Set(days.map(Number));
    if (set.has(value)) set.delete(value);
    else set.add(value);
    onChange({ days: Array.from(set).sort((a, b) => a - b), hour });
  };

  const setPreset = (preset) => onChange({ days: [...preset], hour });

  const dayError = errors?.run_days_of_week || "";

  return (
    <div className="space-y-4">
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <span className={`block ${typography.label} ${textColors.secondary}`}>
            Run days <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>
          </span>
          <div className="flex flex-wrap gap-1" role="group" aria-label="Day presets">
            <GhostButton size="sm" onClick={() => setPreset(ALL_DAYS)} disabled={disabled || sameDays(days, ALL_DAYS)}>
              Every day
            </GhostButton>
            <GhostButton
              size="sm"
              onClick={() => setPreset(WEEKDAY_DAYS)}
              disabled={disabled || sameDays(days, WEEKDAY_DAYS)}
            >
              Weekdays
            </GhostButton>
            <GhostButton
              size="sm"
              onClick={() => setPreset(WEEKEND_DAYS)}
              disabled={disabled || sameDays(days, WEEKEND_DAYS)}
            >
              Weekends
            </GhostButton>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1.5" role="group" aria-label="Run days of the week">
          {WEEKDAYS.map((day) => {
            const active = days.map(Number).includes(day.value);
            return (
              <button
                key={day.value}
                type="button"
                aria-pressed={active}
                aria-label={day.long}
                title={day.long}
                disabled={disabled}
                onClick={() => toggleDay(day.value)}
                className={`${typography.button} px-1 py-2 rounded-lg text-center transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed ${
                  active
                    ? "bg-blue-600 dark:bg-blue-500 text-white"
                    : `${surfaces.tertiary} ${textColors.primary} ${surfaces.hover} border ${borders.default}`
                }`}
              >
                {day.short}
              </button>
            );
          })}
        </div>
        {dayError ? (
          <p className={`mt-1 ${typography.caption} text-red-600 dark:text-red-400`} role="alert">
            {dayError}
          </p>
        ) : (
          <p className={`mt-1 ${typography.caption} ${textColors.tertiary}`}>{formatRunDays(days)}</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Run hour (UTC)" htmlFor="automation-run-hour" required>
          <Select
            id="automation-run-hour"
            value={String(hour ?? "")}
            onChange={(e) => onChange({ days, hour: e.target.value })}
            options={HOUR_OPTIONS}
            placeholder={null}
            disabled={disabled}
            error={errors?.run_hour_utc || ""}
            helperText="The scheduler evaluates the situation at this hour (UTC) on every selected day."
          />
        </FormField>

        <div className={`rounded-2xl border ${borders.glass} ${surfaces.glass} p-3`} aria-live="polite">
          <div className={`${typography.caption} ${textColors.tertiary} uppercase tracking-wider`}>Next run</div>
          {nextRun ? (
            <>
              <div className={`mt-1 ${typography.bodySmall} font-medium ${textColors.primary}`}>
                {formatUtcDateTime(nextRun)}
              </div>
              <div className={`${typography.caption} ${textColors.tertiary}`}>
                {formatRelativeTime(nextRun, now)} · your local time {nextRun.toLocaleString()}
              </div>
            </>
          ) : (
            <div className={`mt-1 ${typography.bodySmall} ${textColors.muted}`}>Pick at least one day.</div>
          )}
        </div>
      </div>

      <p className={`${typography.caption} ${textColors.tertiary}`}>
        Entry-based: a user receives the messages once — when they <strong>enter</strong> the situation — not on
        every run. On the very first run, everyone currently in the situation counts as entering it.
      </p>
    </div>
  );
};

SchedulePicker.propTypes = {
  days: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  hour: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** ({ days, hour }) */
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  errors: PropTypes.object,
};

export default SchedulePicker;
